import { Project, RunningTimer } from "../../lib";
import { useFetcher } from "@remix-run/react";
import { DateTime } from "luxon";
import { Timer } from "./Timer";
import { StartTimerParams } from "~/routes/api.projects.$projectId.start/route";
import { useEffect, useRef, useState } from "react";
import { createFormData, parseForm } from "~/lib/form-parse";
import { CompleteTimerParams } from "~/routes/api.projects.$projectId.completeTimer/route";

export default function ({
  project,
  runningTimer,
}: {
  project?: Project;
  runningTimer?: RunningTimer;
}) {
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const [pendingStartDate, setPendingStartDate] = useState<
    DateTime | undefined
  >();
  const completeFetcher = useFetcher();
  const cancelFetcher = useFetcher();
  const adjustFetcher = useFetcher();

  useEffect(() => {
    if (adjustFetcher.state === "idle" && pendingStartDate !== undefined) {
      adjustTimerAction(pendingStartDate);
    }
  }, [adjustFetcher.state]);

  if (!runningTimer) {
    return <></>;
  }
  let startDate = DateTime.fromSeconds(runningTimer.startTime);
  let expectedEndDate =
    runningTimer.expectedStopTime !== undefined
      ? DateTime.fromSeconds(runningTimer.expectedStopTime)
      : undefined;

  // If currently submitting, optimistically update
  if (adjustFetcher.state !== "idle" && adjustFetcher.formData) {
    const body = parseForm(adjustFetcher.formData, StartTimerParams);
    if (body.timerStartTime !== undefined) {
      startDate = DateTime.fromSeconds(body.timerStartTime);
    }
  }

  const adjustedStartDate = pendingStartDate ?? startDate;

  const completeAction = (stopAtExpectedTime: boolean) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    const formData = createFormData<CompleteTimerParams>({
      stopAtExpectedTime: stopAtExpectedTime,
    });

    completeFetcher.submit(formData, {
      action: `/api/projects/${runningTimer.projectId}/completeTimer`,
      method: "POST",
    });
  };

  const cancelAction = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    cancelFetcher.submit(null, {
      action: `/api/projects/${runningTimer.projectId}/cancelTimer`,
      method: "POST",
    });
  };

  const adjustTimerAction = (newPendingStartDate: DateTime) => {
    if (newPendingStartDate > DateTime.now()) {
      return;
    }
    setPendingStartDate(newPendingStartDate);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // If there's currently a submission, we'll wait for that return before
    // attempting to submit again. This is handled in the useEffect hook above.
    if (adjustFetcher.state !== "idle") {
      return;
    }

    const submitTimer = (startTime: DateTime) => {
      if (!runningTimer) {
        return;
      }
      setPendingStartDate(undefined);

      // This uses the optimistic startDate, so that adjustments stack even if
      // a previous adjustment is in the process of being submitted
      const formData = createFormData<StartTimerParams>({
        completeCurrentTimer: false,
        timerStartTime: startTime.toUnixInteger(),
      });

      adjustFetcher.submit(formData, {
        action: `/api/projects/${runningTimer.projectId}/start`,
        method: "POST",
      });
    };

    debounceTimeoutRef.current = setTimeout(
      () => submitTimer(newPendingStartDate),
      500,
    );
  };

  return (
    <div className="running-timer-container">
      <h1>Running Timer</h1>
      <p>{project?.name ?? "Unknown Project"}</p>
      <Timer startDate={adjustedStartDate} expectedEndDate={expectedEndDate} />

      <button type="submit" onClick={() => completeAction(false)}>
        Complete
      </button>

      <button
        type="submit"
        onClick={() => completeAction(true)}
        disabled={!runningTimer.expectedStopTime}
      >
        Complete At Expected Time
      </button>

      <button type="submit" onClick={cancelAction}>
        Cancel
      </button>

      <br />

      <button
        onClick={() =>
          adjustTimerAction(adjustedStartDate.plus({ minute: -60 }))
        }
      >
        -1 hour
      </button>
      <button
        onClick={() =>
          adjustTimerAction(adjustedStartDate.plus({ minute: -30 }))
        }
      >
        -30 minutes
      </button>
      <button
        onClick={() =>
          adjustTimerAction(adjustedStartDate.plus({ minute: -5 }))
        }
      >
        -5 minutes
      </button>
      <button
        onClick={() => adjustTimerAction(adjustedStartDate.plus({ minute: 5 }))}
      >
        +5 minutes
      </button>
      <button
        onClick={() =>
          adjustTimerAction(adjustedStartDate.plus({ minute: 30 }))
        }
      >
        +30 minutes
      </button>
      <button
        onClick={() =>
          adjustTimerAction(adjustedStartDate.plus({ minute: 60 }))
        }
      >
        +1 hour
      </button>
    </div>
  );
}
